import CompCard from '@app/components/CompCard'
import { DataList, DataListItem } from '@app/components/DataList'
import ExpandableContent from '@app/components/ExpandableContent'
import LotCard from '@app/components/LotCard'
import PhotoStrip from '@app/components/PhotoStrip'
import PillListRenderer from '@app/components/PillListRenderer'
import SignCard from '@app/components/SignCard'
import SignTransferModal from '@app/components/SignTransferModal'
import { SocialConnection } from '@app/components/SocialConnection'
import SpaceCard from '@app/components/SpaceCard'
import { PropertyType } from '@app/models/enums'
import {
  formatCurrency,
  formatDate,
  formatNumber,
  humanize,
} from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardTitle, Row } from 'reactstrap'
import { PropertyPageProps } from './PropertyPage'
import './PropertyPage.scss'

export default class PropertyDetail extends React.PureComponent<PropertyPageProps> {
  public render() {
    return (
      <DocumentTitle title={`${this.props.property.name} :: Details`}>
        <div className='mb-3'>
          <PhotoStrip
            photos={this.props.property.photos}
            propertyId={this.props.property.id}
          />
          {this._renderGeneralInformation()}
          {this._renderOwner()}
          {this._renderHighlights()}
          {this._renderSpaces()}
          {this._renderLots()}
          {this._renderDescriptions()}
          {this._renderSigns()}
          {this._renderComps()}
          {this._renderTransactions()}
          {this._renderRelatedConnections()}
          {this._renderSocialNetworks()}
          {this._renderSuccessStories()}
        </div>
      </DocumentTitle>
    )
  }

  private _getUnitDescription() {
    if (this.props.property.propertyTypes.includes(PropertyType.HealthCare)) {
      return 'Beds'
    }
    if (
      this.props.property.propertyTypes.includes(PropertyType.HotelAndMotel) ||
      this.props.property.propertyTypes.includes(PropertyType.SeniorHousing)
    ) {
      return 'Rooms'
    }
    return 'Units'
  }

  private _renderComps() {
    if (this.props.property.comps.size === 0) {
      return null
    }

    const comps = this.props.property.comps.toArray().map((c, i) => (
      <div className='detail-card' key={`comp-${i}`}>
        <CompCard comp={c} />
      </div>
    ))

    return (
      <div>
        <h5 className='section-title'>Comps</h5>
        <div className='detail-card-group mx-2'>{comps}</div>
      </div>
    )
  }

  private _renderDescriptions() {
    if (
      !this.props.property.propertyDescription &&
      !this.props.property.locationDescription &&
      !this.props.property.zoningDescription
    ) {
      return null
    }

    return (
      <div>
        <h5 className='section-title'>Descriptions</h5>
        {this.props.property.propertyDescription && (
          <div className='mx-3 mb-3'>
            <div className='font-weight-bold'>Property Description</div>
            <ExpandableContent>
              <div className='white-space-pre'>
                {this.props.property.propertyDescription}
              </div>
            </ExpandableContent>
          </div>
        )}
        {this.props.property.locationDescription && (
          <div className='mx-3 mb-3'>
            <div className='font-weight-bold'>Location Description</div>
            <ExpandableContent>
              <div className='white-space-pre'>
                {this.props.property.locationDescription}
              </div>
            </ExpandableContent>
          </div>
        )}
        {this.props.property.zoningDescription && (
          <div className='mx-3 mb-3'>
            <div className='font-weight-bold'>Zoning Description</div>
            <ExpandableContent>
              <div className='white-space-pre'>
                {this.props.property.zoningDescription}
              </div>
            </ExpandableContent>
          </div>
        )}
      </div>
    )
  }

  private _renderGeneralInformation() {
    const property = this.props.property

    return (
      <div>
        <h5 className='section-title'>General Information</h5>
        <DataList className='mx-3'>
          <DataListItem
            label='Building Size'
            value={`${formatNumber(property.totalBuildingSize)} SF`}
          />
          <DataListItem
            label='Building Class'
            value={property.buildingClass}
            visible={property.propertyTypes.includes(PropertyType.Office)}
          />
          <DataListItem
            label='Total Lot Size'
            value={
              property.totalLotSize ? property.totalLotSize.toString() : 'N/A'
            }
          />
          <DataListItem
            label='Occupancy'
            value={property.occupancy > 0 ? `${property.occupancy}%` : 'N/A'}
            visible={
              property.propertyTypes.includes(PropertyType.HealthCare) ||
              property.propertyTypes.includes(PropertyType.Industrial) ||
              property.propertyTypes.includes(PropertyType.Multifamily) ||
              property.propertyTypes.includes(PropertyType.Office) ||
              property.propertyTypes.includes(PropertyType.Retail) ||
              property.propertyTypes.includes(PropertyType.SpecialPurpose) ||
              property.propertyTypes.includes(
                PropertyType.SportAndEntertainment,
              )
            }
          />
          <DataListItem
            label='Year Built'
            value={property.yearBuilt > 0 ? property.yearBuilt : 'N/A'}
          />
          <DataListItem label='APN / Parcel No' value={property.parcelId} />
          <DataListItem
            label='Gross Leaseable Area'
            value={formatNumber(property.grossLeaseableArea)}
            visible={property.propertyTypes.includes(PropertyType.Retail)}
          />
          <DataListItem
            label='Ceiling Height'
            value={`${formatNumber(property.ceilingHeight)} FT`}
            visible={property.propertyTypes.includes(PropertyType.Industrial)}
          />
          <DataListItem
            label='Dock High Doors'
            value={formatNumber(property.noOfDockHighDoorsLoading)}
            visible={property.propertyTypes.includes(PropertyType.Industrial)}
          />
          <DataListItem
            label='Drive in Doors'
            value={formatNumber(property.noOfDriveInGradeLevelDoors)}
            visible={property.propertyTypes.includes(PropertyType.Industrial)}
          />
          <DataListItem
            label={`No of ${this._getUnitDescription()}`}
            value={formatNumber(property.noOfUnits)}
            visible={
              property.propertyTypes.includes(PropertyType.HealthCare) ||
              property.propertyTypes.includes(PropertyType.Multifamily) ||
              property.propertyTypes.includes(PropertyType.HotelAndMotel) ||
              property.propertyTypes.includes(PropertyType.SeniorHousing)
            }
          />
          <DataListItem
            label='Corridor'
            value={humanize(property.corridor)}
            visible={property.propertyTypes.includes(
              PropertyType.HotelAndMotel,
            )}
          />
          <DataListItem
            label='Real Estate Taxes'
            value={formatCurrency(property.financialData.realEstateTaxes)}
            visible={property.financialData.realEstateTaxes > 0}
          />
          <DataListItem
            label='Insurance Expenses'
            value={formatCurrency(property.financialData.insuranceExpenses)}
            visible={property.financialData.insuranceExpenses > 0}
          />
          <DataListItem
            label='Other Expenses'
            value={formatCurrency(property.financialData.otherExpenses)}
            visible={property.financialData.otherExpenses > 0}
          />
          <DataListItem
            label='Gross Rent Multiplier'
            value={formatNumber(property.grossRentMultiplier, 2)}
            visible={
              property.propertyTypes.includes(PropertyType.Multifamily) ||
              property.propertyTypes.includes(PropertyType.ResidentialIncome)
            }
          />
          <DataListItem
            label='Average Daily Rate'
            value={formatCurrency(property.avgDailyRate)}
            visible={property.propertyTypes.includes(
              PropertyType.HotelAndMotel,
            )}
          />
          <DataListItem
            label='Revenue Per PAR'
            value={formatNumber(property.revPerPAR, 2)}
            visible={property.propertyTypes.includes(
              PropertyType.HotelAndMotel,
            )}
          />
          <DataListItem
            label='Average Occupancy'
            value={formatNumber(property.avgOccupancy, 0)}
            visible={property.propertyTypes.includes(
              PropertyType.HotelAndMotel,
            )}
          />
        </DataList>
        <Row className='mx-3' cols={1}>
          <div className='mr-5'>Property Types</div>
          <div className='ml-2'>
            <PillListRenderer
              noLink
              list={property.propertyTypes.map((propType) => {
                return {
                  name: humanize(propType),
                }
              })}
            />
          </div>
        </Row>
      </div>
    )
  }

  private _renderHighlights() {
    if (this.props.property.highlights.size === 0) {
      return null
    }

    const highlights = this.props.property.highlights
      .toArray()
      .map((h, i) => <li key={`highlight-${i}`}>{h}</li>)

    return (
      <div>
        <h5 className='section-title'>Highlights</h5>
        <ul>{highlights}</ul>
      </div>
    )
  }

  private _renderLots() {
    if (this.props.property.lotDetails.size === 0) {
      return null
    }

    const lots = this.props.property.lotDetails.toArray().map((l) => {
      const createListingUrl = `/brokerage/listing/new?property=${this.props.property.id}&lot=${l.id}`

      const cardActions = (
        <a
          className='text-primary'
          onClick={() => this.props.history.push(createListingUrl)}
          role='button'
        >
          Create listing
        </a>
      )

      return (
        <div className='detail-card' key={l.id}>
          <LotCard
            actions={cardActions}
            lot={l}
            propertyId={this.props.property.id}
          />
        </div>
      )
    })

    return (
      <div>
        <h5 className='section-title'>Lots</h5>
        <div className='detail-card-group mx-2'>{lots}</div>
      </div>
    )
  }

  private _renderOwner() {
    return (
      <div>
        <h5 className='section-title'>Owner(s)</h5>
        <div className='mx-3'>
          <PillListRenderer
            list={this.props.property.owners}
            baseNavigateUrl='/connection'
            idValue='contactId'
          />
        </div>
      </div>
    )
  }

  private _renderRelatedConnections() {
    return (
      <div>
        <h5 className='section-title'>Related Connections</h5>
        <div className='mx-3'>
          <PillListRenderer
            list={this.props.property.associated}
            baseNavigateUrl='/connection'
            idValue='contactId'
          />
        </div>
      </div>
    )
  }

  private _renderSigns() {
    const signs = this.props.property.signs.toArray().map((s, i) => {
      const pendingQty = s.pendingTransfers.toArray().reduce((sum, pending) => {
        sum += pending.quantity
        return sum
      }, 0)

      const availableToTransfer = s.quantity - pendingQty

      const action =
        this.props.permissions.canEdit === true && availableToTransfer > 0 ? (
          <Button
            className='ml-2'
            color='secondary'
            onClick={() =>
              this.props.showSignTransferModal(
                this.props.property,
                s.id,
                s.signType,
                availableToTransfer,
              )
            }
            outline
            size='sm'
            title='Transfer Sign'
          >
            <span className='fa-layers fa-fw'>
              <FontAwesomeIcon icon={['far', 'person-dolly-empty']} />
              <FontAwesomeIcon
                icon={['far', 'sign']}
                transform='shrink-4 up-1 right-7 rotate--15'
              />
            </span>
          </Button>
        ) : null

      return (
        <div className='detail-card' key={`sign-${i}`}>
          <SignCard
            actions={action}
            canEdit={this.props.permissions.canEdit}
            sign={s}
          />
        </div>
      )
    })

    return (
      <div>
        <h5 className='section-title'>Signs</h5>
        <div className='detail-card-group'>{signs}</div>
        <SignTransferModal />
      </div>
    )
  }

  private _renderSocialNetworks() {
    if (this.props.property.social.size === 0) {
      return null
    }

    const socialNetworks = this.props.property.social
      .toArray()
      .map((s) => (
        <SocialConnection
          className='d-inline-block'
          key={`social-${s.type}`}
          socialConnection={s}
        />
      ))

    return (
      <div>
        <h5 className='section-title'>Social Networks</h5>
        <div className='mx-3'>{socialNetworks}</div>
      </div>
    )
  }

  private _renderSpaces() {
    if (this.props.property.spaceDetails.size === 0) {
      return null
    }

    const spaces = this.props.property.spaceDetails.toArray().map((s) => {
      const createListingUrl = `/brokerage/listing/new?property=${this.props.property.id}&space=${s.id}`

      const cardActions = (
        <a
          className='text-primary'
          onClick={() => this.props.history.push(createListingUrl)}
          role='button'
        >
          Create listing
        </a>
      )

      return (
        <div className='detail-card' key={s.id}>
          <SpaceCard
            actions={cardActions}
            propertyId={this.props.property.id}
            space={s}
          />
        </div>
      )
    })

    return (
      <div>
        <h5 className='section-title'>Spaces</h5>
        <div className='detail-card-group mx-2'>{spaces}</div>
      </div>
    )
  }

  private _renderSuccessStories() {
    if (this.props.property.successStories.size === 0) {
      return null
    }

    const successStories = this.props.property.successStories
      .toArray()
      .map((s, index) => {
        return (
          <div className='detail-card' key={`sign-${index}`}>
            <Card>
              <CardBody>
                <CardTitle>
                  <Link to={`/properties/successStory/${s.id}`}>{s.title}</Link>
                </CardTitle>
                {s.description.length > 150 ? (
                  <span>{s.description.substring(0, 150).trimRight()}...</span>
                ) : (
                  s.description
                )}
              </CardBody>
            </Card>
          </div>
        )
      })

    return (
      <div>
        <h5 className='section-title'>Success Stories</h5>
        <div className='detail-card-group'>{successStories}</div>
      </div>
    )
  }

  private _renderTransactions() {
    if (this.props.property.transactionHistory.size === 0) {
      return null
    }

    const transactions = this.props.property.transactionHistory
      .toArray()
      .map((t, i) => (
        <li key={`transaction-${i}`}>
          {formatDate(t.saleDate, 'MM/DD/YYYY')}
          <span> - </span>
          {formatCurrency(t.salePrice)}
          <span> - </span>
          <PillListRenderer
            list={t.owners}
            baseNavigateUrl='/connection'
            idValue='contactId'
          />
        </li>
      ))

    return (
      <div>
        <h5 className='section-title'>Transaction History</h5>
        <ul>{transactions}</ul>
      </div>
    )
  }
}
